import React, { useState, useEffect, useRef } from "react";
import "./MasterLayout.css";
import { resolveUmatAvatarSync, getAvatarPlaceholder, isLocalhost, getUmatByEmail} from "../utils/wkCommon";
import { getUser, isLogged, getJwt, clearAuth, setAuth, isJwtExpired } from "../utils/auth";
import { checkHasPin  } from "../utils/PINService";
import { getLang, setLang, LANG_ID, LANG_ZH, isZh } from "../utils/language";
import { initFontLevel, setFontLevel, getFontLevel } from "../utils/fontSize";

import { useLocation } from "react-router-dom";
import { logVisit } from "../utils/wkCommon";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MasterLayout({ children }) {
  const [fontLevel, setFontLevelState] = useState(initFontLevel());

  const [serverDown, setServerDown] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();

  const showDev = isLocalhost();
  
  const [isUnverifiedUser, setIsUnverifiedUser] = useState(false);

  const [lang, setLangState] = React.useState(getLang());

  const location = useLocation();

  useEffect(() => {
    logVisit({
      path: location.pathname,
      query: location.search,
    });
  }, [location.pathname]);

  function onFontChange(e) {
    const level = parseInt(e.target.value, 10);
    setFontLevel(level);
    setFontLevelState(level);
  }

  function toggleLang() {
    const newLang = lang === LANG_ZH ? LANG_ID : LANG_ZH;
    setLang(newLang);
    window.location.reload();
  }

  /* UNTUK UNVERIFIED USER, LAKUKAN FETCH ULANG UNTUK MEMASTIKAN JIKA SUDAH DIVERIFIKASI, MENCEGAH BLANK PAGE */
  useEffect(() => {
    async function refreshUmatIfMissing() {
      const u = getUser();
      const jwt = getJwt();

      if (!jwt || !u) return;

      if (!u?.umat && u.email) {
        const KEY = "umat_refresh_last_try";
        const now = Date.now();

        const lastTry = parseInt(localStorage.getItem(KEY) || "0", 10);
        const FIVE_MIN = 5 * 60 * 1000;

        // ⛔ kalau belum lewat 5 menit → skip
        if (now - lastTry < FIVE_MIN) {
          console.log("Skip refresh umat (cooldown aktif)");
          return;
        }

        // simpan waktu try sekarang
        localStorage.setItem(KEY, String(now));

        try {
          const umat = await getUmatByEmail(u.email);

          if (umat) {
            const updatedUser = {
              ...u,
              umat: umat.attributes || umat,
            };

            setAuth({ jwt, user: updatedUser });

            // ✅ reset cooldown kalau sukses
            localStorage.removeItem(KEY);

            window.location.reload();
          } else {
            console.warn("Umat belum tersedia, akan retry nanti");
          }
        } catch (e) {
          console.warn("refresh umat failed", e);
        }
      }
    }

    refreshUmatIfMissing();
  }, []);

  useEffect(() => {

    function onBackendDown(){
      setServerDown(true);
    }

    window.addEventListener("backendDown", onBackendDown);

    return () => {
      window.removeEventListener("backendDown", onBackendDown);
    };

  }, []);

  useEffect(() => {
    const jwt = getJwt();
    if (jwt && isJwtExpired(jwt)) {
      clearAuth();
      window.location.reload();
      return;
    }

    const u = getUser();
    
    if (getJwt() && u && !u?.umat) {
      setIsUnverifiedUser(true);
    }

    if (u) {
      setUser({
        name: u?.umat?.displayName || u?.username || u?.email,
        avatar: u?.umat
          ? resolveUmatAvatarSync(u?.umat, "avatarpic")
          : getAvatarPlaceholder(u?.umat?.gender || ""),
        email: u?.email || "",
        role: "",
        gender: u?.umat?.gender || "",
      });
    }
  }, []);

  // state baru untuk PIN
  const [hasPin, setHasPin] = useState(null); // null = belum dicek
  // efek untuk memeriksa apakah user sudah punya PIN (jalankan setelah user tersedia)
  useEffect(() => {
    let mounted = true;
    async function checkPin() {
      setHasPin(null);

      const u = getUser();
      if (!u) {
        setHasPin(false);
        return;
      }

      try {
        const exists = await checkHasPin();
        if (mounted) setHasPin(!!exists);
      } catch (e) {
        if (mounted) setHasPin(false);
      }
    }
    checkPin();
    return () => { mounted = false; };
  }, []);

  // close menu when clicking outside
  useEffect(() => {
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const login = async () => {
    if (isLocalhost()) {
      const defaultUser = {
        id: 1,
        username: "devuser",
        email: "wk35077@gmail.com",
        displayName: "Dev User",
        roles: ["authenticated"],
      };

      const jwt = process.env.REACT_APP_LOCAL_JWT || "dev-jwt-token-0123456789";

      // Optimistically set UI so button feedback is immediate
      setUser({
        name: defaultUser.displayName || defaultUser.username,
        avatar: "https://i.pravatar.cc/80",
        email: defaultUser.email,
        role: defaultUser.role,
      });

      // Persist legacy keys (keep parity with LoginRedirect behavior)
      try {
        localStorage.setItem("jwt", jwt);
        localStorage.setItem("username", defaultUser.username);
        localStorage.setItem("email", defaultUser.email);
      } catch (e) {
        console.warn("localStorage set failed (dev login)", e);
      }

      // ------------- NEW: await umat lookup and attach to user -------------
      try {
        // ensure getUmatByEmail runs with the currently-stored jwt (some wkCommon variants use getJwt())
        const umatRecord = await getUmatByEmail(defaultUser.email);
        let umat = null;
        if (umatRecord) {
          umat = umatRecord.attributes ? umatRecord.attributes : umatRecord;
          // store raw umatData + umatID like LoginRedirect does
          try { localStorage.setItem('umatData', JSON.stringify(umat)); } catch (e) {}
          const kode = umat.umatID || umat.kodeUmat || umat.kode || umat.id || null;
          if (kode) {
            try { localStorage.setItem('umatID', String(kode)); } catch (e) {}
          }
        }

        // build final user object (attach umat if available)
        const finalUser = {
          ...defaultUser,
          umat: umat || null,
        };

        // store canonical auth AFTER attaching umat (this keeps getUser().umat available immediately)
        try {
          setAuth({ jwt, user: finalUser });
          localStorage.setItem('lefy_auth', JSON.stringify({ jwt, user: finalUser }));
        } catch (e) {
          console.warn("setAuth/lefy_auth write failed (dev login)", e);
        }

        // update UI with any richer info
        setUser({
          name: finalUser.displayName || finalUser.username,
          avatar: finalUser.avatar || "https://i.pravatar.cc/80",
          email: finalUser.email,
          role: (finalUser.roles && finalUser.roles[0]) || "",
        });

        window.location.reload();
      } catch (e) {
        // if umat lookup fails, still set canonical auth (without umat) so app won't crash
        console.warn("getUmatByEmail failed in dev login — continuing without umat:", e);
        const fallbackUser = { ...defaultUser, umat: null };
        try {
          setAuth({ jwt, user: fallbackUser });
          localStorage.setItem('lefy_auth', JSON.stringify({ jwt, user: fallbackUser }));
        } catch (err) {
          console.warn("setAuth/lefy_auth write failed in fallback path", err);
        }
      }

      // finished local dev login (keeps flow local)
      return;
    }

    window.location.href = `${API_URL}/api/connect/google`;
  };


  const logout = () => {
    clearAuth();
    setUser(null);
    setMenuOpen(false);
    window.location.reload();
  };

  // menu actions -> navigate to app routes (you can implement these routes/pages)
  function openProfileDetails() {
    setMenuOpen(false);
    // route to profile details page (implement this page to show the profile detail card)
    window.location.href = "/me";
  }

  function openQrLiEn() {
    setMenuOpen(false);
    // route to QR Li En page (implement to render the blue QR card using user's umatID)
    window.location.href = "/myqr";
  }

  function openQrPusat() {
    setMenuOpen(false);
    // route to QR Pusat page (implement to render the green QR card using tsfyUmatID)
    window.location.href = "/myqrpusat";
  }

  function openPinCreate() {
    setMenuOpen(false);
    window.location.href = "/pin/create";
  }

  function openPinChange() {
    setMenuOpen(false);
    window.location.href = "/pin/change";
  }

  return (
    <div className="lefy-page">
      {/* Banner (always visible) */}
      <div className="lefy-banner">
        {serverDown && (
          <div style={{
            position: "fixed",
            top: 110,
            left: 0,
            width: "100%",
            gap: "1rem",
            background:"#fff7e6",
            color:"#924b00",
            textAlign:"center",
            padding:"4px 8px",
            fontSize:"0.9rem", display: "flex"
          }}>
            <div>⚠️</div>
            <div style={{textAlign: "left"}}>
              伺服器暫時無法使用，請檢查您的網路連線。<br/>Server sedang tidak tersedia, cek koneksi internet Anda.
            </div>
          </div>
        )}
        <div className="chars">禮恩佛院{showDev && <span className="dev-badge"> [Dev]</span>}</div>
        <div className="topbar-right">
          <div className="font-slider">
            <span className="font-small">A</span>
            <input
              type="range"
              min="1"
              max="5"
              step="1"
              value={fontLevel}
              onChange={onFontChange}
            />
            <span className="font-large">A</span>
          </div>

          <button className="btn-icon" style={{ color: "#fff" }} onClick={toggleLang} title="切換語言 Ubah Bahasa">
            {lang === LANG_ZH ? "ID" : "文"}
          </button>

          {!user ? (
            !isUnverifiedUser ? (
              <button className="lefy-btn-google" onClick={login} aria-label="Login with Google">
                <img src="https://developers.google.com/identity/images/g-logo.png" alt="G" />
                <span>{loginLoading ? (isZh() ? "登入中…" : "Logging in…") : (isZh() ? "登入" : "Login")}</span>
              </button>
              ) :(
              <button className="lefy-btn-google" onClick={logout}>
                { isZh() ? "登出" : "Logout"}
              </button>
            )
          ) : (
            <div className="avatar-wrapper" ref={menuRef}>
              <button
                className="avatar-button"
                onClick={() => setMenuOpen((s) => !s)}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                title={user.name}
              >
                <img src={user.avatar} alt={user.name || "avatar"} className="avatar-img" />
              </button>

              {menuOpen && (
                <div className="profile-popup" role="menu" aria-label="User menu">
                  <div className="popup-userinfo">
                    <div className="popup-name">{user.name}</div>
                    <div className="popup-email">{user.email}</div>
                  </div>

                  <div className="popup-actions">
                    <button className="popup-btn" onClick={openProfileDetails}>
                      { isZh() ? "我個人資料" : "Profil Saya"} 
                    </button>
                    <button className="popup-btn" onClick={openQrLiEn}>
                      { isZh() ? "禮恩 QR 碼" : "Tampil QR Li En"}
                    </button>
                    { getUser()?.umat?.tsfyUmatID && (
                      <button className="popup-btn" onClick={openQrPusat}>
                        { isZh() ? "中心 QR 碼" : "Tampil QR Pusat"}
                      </button>
                    )}

                    {/* divider tipis */}
                    <div className="popup-divider" aria-hidden="true" />

                    {/* menu kondisional untuk PIN */}
                    {hasPin === null ? (
                      <button className="popup-btn" disabled style={{ opacity: 0.6, cursor: "default" }}>
                        { isZh() ? "驗證 PIN…" : "Memeriksa PIN…"}
                      </button>
                    ) : hasPin === true ? (
                      <button className="popup-btn" onClick={openPinChange}>
                        { isZh() ? "更改 PIN" : "Ubah PIN"}
                      </button>
                    ) : (
                      <button className="popup-btn" onClick={openPinCreate}>
                        { isZh() ? "建立 PIN" : "Buat PIN"}
                      </button>
                    )}

                    <button className="popup-btn logout" onClick={logout}>
                      { isZh() ? "登出" : "Logout"} 
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="lefy-main">
        {(!user || isUnverifiedUser) ? (
          <>

            {/* Status Message */}
            <div className="lefy-login-required" style={{ marginTop: 10 }}>
              {!user ? (
                <p>
                  { isZh() 
                    ? "請先登入以開始使用" 
                    : "Silakan login untuk memulai akses data."}
                </p>
              ) : (
                <p>
                  { isZh() 
                    ? "登入成功。您的帳號正在等待管理員驗證，請稍後再試。" 
                    : "Login berhasil. Akun Anda sedang menunggu verifikasi admin. Silakan coba kembali secara berkala."}
                </p>
              )}
            </div>

            {/* Language Hint */}
            <div style={{
              marginTop: 10,
              maxWidth: 820,
              marginLeft: 'auto',
              marginRight: 'auto',
              background: '#eef6ff',
              border: '1px solid #bcd7ff',
              color: '#1e3a8a',
              padding: '12px 16px',
              borderRadius: 10,
              fontSize: '0.95rem',
              textAlign: 'center',
              lineHeight: 1.5
            }}>
              <div>
                Aplikasi ini mendukung dua bahasa: Bahasa Indonesia dan 中文。 
                Silakan klik tombol bahasa di pojok kanan atas untuk mengganti bahasa.
              </div>
              <div style={{ marginTop: 6 }}>
                本應用支援兩種語言：中文與印尼語。  
                請點擊右上角的語言按鈕切換顯示語言。
              </div>
            </div>

            <div className="lefy-disclaimer-wrap">
              <div className="lefy-disclaimer">
                <div className="warn" aria-hidden="true">⚠️</div>
                <div className="text">
                  {isZh()
                    ? "本應用程式內之資料僅供內部使用，未經許可，不得於課程／活動行政用途以外之情況下對外散佈。"
                    : "Data dalam aplikasi ini hanya digunakan untuk keperluan internal, dilarang menyebarkan tanpa ijin di luar keperluan administrasi kelas/kegiatan."}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="lefy-main-container">{children}</div>
          </>
        )}
      </div>

      {/* Blue footer bar fixed at bottom */}
      <div className="lefy-footer-strip-fixed" />
    </div>
  );
}
